import { useContext, useState } from 'react'
import moment from 'moment-timezone'
import { enqueueSnackbar } from 'notistack'
import _ from 'lodash'

import { Button } from '@mui/material'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import MobileOffOutlinedIcon from '@mui/icons-material/MobileOffOutlined'
import AllInclusiveOutlinedIcon from '@mui/icons-material/AllInclusiveOutlined'

import { AppContext } from '/providers/app-context'
import { updatePass } from '/providers/passes-api'

import StyledPaper from '/components/paper'
import ExpiryDialog from '/components/passes/expiry-dialog'
import ClickToCopy from '/components/click-to-copy'

const DetailSummary = ({ template, pass, classes }) => {

  const { apiSource, apiKeys, setProcessing, refreshPass, client } = useContext(AppContext)
  const now = moment().format("YYYYMMDDHHmmss")

  const [calendarOpen, setCalendarOpen] = useState(false)

  if (!template || !pass) return null

  const isSynchronised = template.last_modified_timestamp < pass.last_modified_apple_timestamp && template.last_modified_timestamp < pass.last_modified_google_timestamp

  const synchronise = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Synchronising pass', variant: 'light' })
    await updatePass({ apiSource, apiKeys, templateId: template.id, passId: pass.pass_id, instance_data: pass.instance_data })
    enqueueSnackbar({ message: 'Pass synchronised', variant: 'success' })
    setProcessing(false)

    refreshPass({ templateId: template.id, passId: pass.pass_id })
  }

  const expireNow = async () => {
    setExpiryDate(moment().format("YYYYMMDDHHmmss"))
  }

  const neverExpire = async () => {
    setExpiryDate(null)
  }

  const setExpiryDate = async date => {
    if (date) _.set(pass.instance_data, "__pass_validity__.__end__", date)
    else _.unset(pass.instance_data, "__pass_validity__.__end__")

    setProcessing(true)
    enqueueSnackbar({ message: 'Updating expiry date', variant: 'light' })
    const result = await updatePass({ apiSource, apiKeys, templateId: template.id, passId: pass.pass_id, instance_data: pass.instance_data })
    if (result.error) {
      enqueueSnackbar({ message: 'Expiry date updated failed', variant: 'error' })
    } else {
      enqueueSnackbar({ message: 'Expiry date updated', variant: 'success' })
    }
    setProcessing(false)

    refreshPass({ templateId: template.id, passId: pass.pass_id })
  }

  return (
    <div className={classes}>
      <StyledPaper>
        {dataItem("Pass id", <ClickToCopy>{pass.pass_id}</ClickToCopy>)}
        <br />
        {dataItem("Created", dateTimeFormat(pass.creation_timestamp))}
        {dataItem("Last Modified", dateTimeFormat(Math.max(pass.last_modified_apple_timestamp, pass.last_modified_google_timestamp)))}
        <br />
        {dataItem("Expires", yesNoFormat(!pass.expiry_date || pass.expiry_date > now, expiryDateTimeFormat(pass.expiry_date), "expired"))}
        <br />
        {dataItem("Grouping id", pass.grouping_id)}
        {dataItem("Booking Reference", pass.booking_reference)}
        <br />
        {dataItem("Template id", pass.template_id)}
        {dataItem("Synchronised", yesNoFormat(isSynchronised, "yes", "no"))}
        {dataItem("Template last Modified", dateTimeFormat(template.last_modified_timestamp))}
      </StyledPaper>

      <StyledPaper classes="mt-5">
        <div className='flex flex-row w-full justify-between'>
          <div>
            <Button onClick={() => setCalendarOpen(true)}><CalendarMonthOutlinedIcon className='mr-5' />Change Expiry Date</Button>
            <Button onClick={neverExpire} disabled={(pass.mode === "test" && [!0, 2].includes(client.client_id)) || !pass.expiry_date}><AllInclusiveOutlinedIcon className='mr-5' />Never Expire</Button>
            <Button onClick={expireNow} disabled={pass.expiry_date && pass.expiry_date < now}><MobileOffOutlinedIcon className='mr-5' />Expire Immediately</Button>
          </div>
          <Button onClick={synchronise} disabled={isSynchronised}><SyncOutlinedIcon className='mr-5' />Synchronise</Button>
        </div>
      </StyledPaper>

      <ExpiryDialog open={calendarOpen} setOpen={setCalendarOpen} expiryDate={pass.expiry_date} setExpiryDate={setExpiryDate} />
    </div>
  )
}

export default DetailSummary

const dataItem = (title, value) => <div className='flex justify-between w-full gap-10 mb-3'><div className='text-nowrap'>{title}</div><div className='font-bold'>{value}</div></div>
const expiryDateTimeFormat = datestring => datestring ? moment(datestring, "YYYYMMDDHHmmss").format("DD MMM YYYY HH:mm") : "no expiry"
const dateTimeFormat = datestring => moment(datestring, "YYYYMMDDHHmmss").format("DD MMM YYYY HH:mm")
const yesNoFormat = (value, yes, no) => value ? <span className='green'>{yes}</span> : <span className='red'>{no}</span>